import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Spinner from '@rio-cloud/rio-uikit/Spinner';

import { useGetVehiclesQuery } from '@/services/dfpFrontendApi';
import ArbitraryInputModal, { type ArbitraryTarget } from './components/modals/ArbitraryInputModal';
import FileViewerModal from './components/modals/FileViewerModal';
import MessageInspectorModal from './components/modals/MessageInspectorModal';
import RuntimeStatsBar from './components/RuntimeStatsBar/RuntimeStatsBar';
import VehicleCard from './components/VehicleList/VehicleCard';
import VehicleSearchBar from './components/VehicleList/VehicleSearchBar';
import type { DataMockFileMeta, ScheduleSummary } from './types';
import { getNextFireTime } from './utils/useCountdown';

interface InspectorTarget {
    vin: string;
    schedule: ScheduleSummary;
}

interface FileViewerTarget {
    vin: string;
    file: DataMockFileMeta;
}

const DemoFleetPlayerPage = () => {
    const intl = useIntl();
    const { data: vehicles, isLoading, error } = useGetVehiclesQuery();

    const [search, setSearch] = useState('');
    const [expandedVin, setExpandedVin] = useState<string | null>(null);
    const [now, setNow] = useState(new Date());

    const [arbitraryOpen, setArbitraryOpen] = useState(false);
    const [arbitraryTarget, setArbitraryTarget] = useState<ArbitraryTarget | null>(null);
    const [inspectorTarget, setInspectorTarget] = useState<InspectorTarget | null>(null);
    const [fileViewerTarget, setFileViewerTarget] = useState<FileViewerTarget | null>(null);

    // Tick the clock for runtime variables display
    useState(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    });

    if (isLoading) {
        return (
            <div className="display-flex justify-content-center padding-top-50">
                <Spinner />
            </div>
        );
    }

    if (error || !vehicles) {
        return (
            <div className="container-fluid padding-top-20 text-center text-color-danger">
                <FormattedMessage id="intl-msg:common.error.failedToLoadVehicles" />
            </div>
        );
    }

    const filteredVehicles = vehicles.filter(
        (v) =>
            v.asset_name.toLowerCase().includes(search.toLowerCase()) ||
            v.vin.toLowerCase().includes(search.toLowerCase()),
    );

    const nextOverallVin =
        vehicles
            .flatMap((v) => v.schedules.map((s) => ({ vin: v.vin, next: getNextFireTime(s.cron) })))
            .filter((x): x is { vin: string; next: number } => x.next !== null)
            .sort((a, b) => a.next - b.next)[0]?.vin ?? null;

    return (
        <>
            <MessageInspectorModal target={inspectorTarget} onClose={() => setInspectorTarget(null)} />
            <FileViewerModal target={fileViewerTarget} onClose={() => setFileViewerTarget(null)} />
            <ArbitraryInputModal
                isOpen={arbitraryOpen}
                onClose={() => {
                    setArbitraryOpen(false);
                    setArbitraryTarget(null);
                }}
                vehicles={vehicles}
                initialValues={arbitraryTarget}
            />

            <div className="container-fluid padding-top-20 padding-bottom-20" style={{ maxWidth: 1100 }}>
                <RuntimeStatsBar now={now} />

                <div className="display-flex align-items-stretch gap-10 margin-bottom-15">
                    <div className="flex-1-1">
                        <VehicleSearchBar search={search} onSearchChange={setSearch} totalCount={vehicles.length} />
                    </div>
                    <Button bsStyle="primary" multiline onClick={() => setArbitraryOpen(true)}>
                        <span className="uikit-icon icon-send margin-right-5" />
                        <FormattedMessage id="intl-msg:arbitraryInput.button" />
                    </Button>
                </div>

                {filteredVehicles.map((vehicle) => (
                    <VehicleCard
                        key={vehicle.vin}
                        vehicle={vehicle}
                        isExpanded={expandedVin === vehicle.vin}
                        isNextOverall={vehicle.vin === nextOverallVin}
                        onToggleExpand={() => setExpandedVin(expandedVin === vehicle.vin ? null : vehicle.vin)}
                        onViewMessageConfig={(schedule) => setInspectorTarget({ vin: vehicle.vin, schedule })}
                        onOpenSourceFile={(file) => setFileViewerTarget({ vin: vehicle.vin, file })}
                        onOpenInArbitrary={(schedule, mergedJson, overriddenJsonPaths) => {
                            setArbitraryTarget({
                                vin: vehicle.vin,
                                cid: schedule.cid,
                                version: schedule.version,
                                jsonContent: mergedJson,
                                overriddenJsonPaths,
                            });
                            setArbitraryOpen(true);
                        }}
                    />
                ))}
            </div>
        </>
    );
};

export default DemoFleetPlayerPage;
